import { ApiError } from "./api-error";

export type RequestMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

type RequestOptions<TBody> = {
  url: string;
  method?: RequestMethod;
  body?: TBody;
  headers?: Record<string, string>;
};

async function parseResponse<TResponse>(response: Response): Promise<TResponse> {
  if (response.status === 204) {
    return undefined as TResponse;
  }

  const contentType = response.headers.get("content-type") ?? "";
  const text = await response.text();

  if (!text) {
    return undefined as TResponse;
  }

  if (contentType.includes("application/json")) {
    return JSON.parse(text) as TResponse;
  }

  return text as TResponse;
}

export async function request<TResponse, TBody = never>({
  url,
  method = "GET",
  body,
  headers = {},
}: RequestOptions<TBody>): Promise<TResponse> {
  const requestHeaders: Record<string, string> = {
    "x-correlation-id": crypto.randomUUID(),
    "x-region": import.meta.env.VITE_X_REGION,
    ...headers,
  };

  const config: RequestInit = {
    method,
    credentials: "include",
    headers: requestHeaders,
  };

  if (body !== undefined) {
    if (body instanceof FormData) {
      config.body = body;
      delete requestHeaders["Content-Type"];
    } else {
      requestHeaders["Content-Type"] = "application/json";
      config.body = JSON.stringify(body);
    }
  }

  const response = await fetch(url, config);

  if (!response.ok) {
    const errorData = await parseResponse<unknown>(response).catch(() => ({}));
    throw new ApiError("REQUEST FAILED", response.status, errorData);
  }

  return parseResponse<TResponse>(response);
}
